/**
 * Tenant → Settings → Billing → Subscription Invoices
 *
 * Lists the caller-tenant's own subscription invoices (auto-generated from
 * Razorpay `payment_captured` webhooks) with a one-click PDF download.
 *
 * Route: /app/settings/billing/invoices
 * Access: any authenticated tenant user (the backend RBAC scopes results to
 * the caller's tenant_id).
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { authDownload } from "@/lib/authDownload";
import { fmtDateTime } from "@/lib/time";
import { PageHeader, GoldButton, GhostButton } from "@/components/Ui";
import { Receipt, Download, RefreshCw, FileText } from "lucide-react";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function SubscriptionInvoices() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/tenant/subscription-invoices");
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const download = async (invoice) => {
        setDownloading(invoice.id);
        try {
            await authDownload(
                `/tenant/subscription-invoices/${invoice.id}/pdf`,
                { filename: `${(invoice.invoice_no || "invoice").replace(/\//g, "-")}.pdf` }
            );
            toast.success(`Downloaded ${invoice.invoice_no}`);
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setDownloading(null);
        }
    };

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="tenant-subscription-invoices">
            <PageHeader
                Icon={Receipt}
                title="Subscription invoices"
                subtitle="GST-compliant tax invoices for every Law OS subscription payment. Auto-generated when Razorpay captures your payment."
            />
            <div className="flex items-center justify-end mb-4">
                <GhostButton onClick={load} disabled={loading} data-testid="sub-invoices-refresh">
                    <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`}/>
                    {loading ? "Loading…" : "Refresh"}
                </GhostButton>
            </div>

            {loading ? (
                <div className="border border-border rounded-2xl bg-card p-12 text-center text-sm text-muted-foreground" data-testid="sub-invoices-loading">
                    Loading invoices…
                </div>
            ) : rows.length === 0 ? (
                <div className="border border-border rounded-2xl bg-card p-12 text-center" data-testid="sub-invoices-empty">
                    <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3"/>
                    <div className="text-sm text-foreground">No subscription invoices yet</div>
                    <div className="text-xs text-muted-foreground mt-1">
                        Your first invoice is generated automatically once your Razorpay payment is captured.
                    </div>
                </div>
            ) : (
                <div className="border border-border rounded-2xl bg-card overflow-x-auto" data-testid="sub-invoices-list">
                    <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground border-b border-border">
                            <tr>
                                <th className="text-left px-4 py-3">Invoice #</th>
                                <th className="text-left px-4 py-3">Plan</th>
                                <th className="text-left px-4 py-3">Issued</th>
                                <th className="text-right px-4 py-3">Subtotal</th>
                                <th className="text-right px-4 py-3">GST</th>
                                <th className="text-right px-4 py-3">Total</th>
                                <th className="text-left px-4 py-3">Status</th>
                                <th className="text-right px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.id} className="border-b border-card hover:bg-background/50"
                                    data-testid={`sub-invoice-row-${r.invoice_no}`}>
                                    <td className="px-4 py-3 font-mono text-xs text-primary">{r.invoice_no}</td>
                                    <td className="px-4 py-3 text-foreground">
                                        {r.plan_name}
                                        {r.kind === "wallet_topup" && (
                                            <span className="ml-2 text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded border border-primary/40 text-primary align-middle">
                                                wallet
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap text-xs">
                                        {fmtDateTime(r.issued_at)}
                                    </td>
                                    <td className="px-4 py-3 text-right text-muted-foreground">{inr(r.subtotal_inr)}</td>
                                    <td className="px-4 py-3 text-right text-muted-foreground">
                                        {inr(r.gst_amount_inr)}
                                        <span className="text-[10px] text-muted-foreground/70 ml-1">
                                            @ {r.gst_rate_pct}%
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-right font-medium text-foreground">{inr(r.total_inr)}</td>
                                    <td className="px-4 py-3">
                                        <span className="text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded border border-emerald-500/40 text-emerald-500">
                                            {r.status || "paid"}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <GoldButton
                                            onClick={() => download(r)}
                                            disabled={downloading === r.id}
                                            data-testid={`sub-invoice-download-${r.invoice_no}`}
                                        >
                                            <Download className="w-3.5 h-3.5 mr-1"/>
                                            {downloading === r.id ? "…" : "PDF"}
                                        </GoldButton>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="mt-6 text-[11px] text-muted-foreground border border-border rounded-lg p-3 bg-background/50">
                <strong className="text-foreground">Where does this come from?</strong> Every time your Razorpay subscription payment is captured, Law OS generates a GST-compliant tax invoice at 18% and stores it here. Retries never create duplicates.
            </div>
        </div>
    );
}
